import { useState, useEffect } from "react";
import { apiUrl } from "../config/api";

// Convert new Supabase property structure
// into the structure your existing property cards expect.
const normalizeProperty = (property, index) => {
  const reference =
    property.reference ||
    property.Reference ||
    property.id ||
    `property-${index}`;

  const gallery = Array.isArray(property.gallery)
    ? property.gallery
    : [];

  const image = property.image_url || property.imageUrl || "";

  return {
    ...property,

    // Existing component fields
    Reference: reference,

    PropertyType: {
      NameType:
        property.title ||
        property.PropertyType?.NameType ||
        "Property",
    },

    Pictures: {
      Picture: image ? [{ PictureURL: image }] : [],
    },

    Gallery: gallery,
    ShortDescription: property.short_description || property.shortDescription || "",
    Description: property.description || property.Description || "",
    Features: Array.isArray(property.features) ? property.features : [],

    Price:
      property.price ??
      property.Price ??
      0,

    Location:
      property.location ||
      property.Location ||
      "",

    Province:
      property.province ||
      property.Province ||
      "",

    Bedrooms:
      property.bedrooms ??
      property.Bedrooms ??
      0,

    Bathrooms:
      property.bathrooms ??
      property.Bathrooms ??
      0,

    Built:
      property.area ?? property.Built ?? 0,

    slug:
      property.slug ||
      String(reference).toLowerCase(),
  };
};


// ============================================================
// GET PROPERTIES
// ============================================================

export const useProperties = (listingType = "buy") => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = listingType === "all"
          ? apiUrl("/api/properties")
          : apiUrl(`/api/properties?listing_type=${encodeURIComponent(listingType)}`);

        console.log("Fetching properties from:", url);

        const res = await fetch(url, {
          headers: {
            "ngrok-skip-browser-warning": "true",
          },
        });

        console.log("Properties status:", res.status);

        const data = await res.json();

        console.log("Properties response:", data);

        if (!res.ok || data?.error) {
          throw new Error(
            data?.error ||
              "Unable to load properties."
          );
        }

        const records = Array.isArray(data?.properties)
          ? data.properties
          : Array.isArray(data?.Property)
          ? data.Property
          : [];

        const normalized = records.map(normalizeProperty);

        console.log(
          `Loaded ${normalized.length} ${listingType} properties`
        );

        setProperties(normalized);
      } catch (err) {
        console.error(
          "Failed to load properties:",
          err
        );

        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [listingType]);

  return {
    properties,
    loading,
    error,
  };
};


// ============================================================
// GET SINGLE PROPERTY
// ============================================================

export const useProperty = (ref, listingType = "buy") => {
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ref) {
      setLoading(false);
      return;
    }

    const fetchProperty = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(apiUrl(`/api/properties?listing_type=${encodeURIComponent(listingType)}`), {
          headers: { "ngrok-skip-browser-warning": "true" },
        });

        console.log(
          "Single property status:",
          res.status
        );

        const data = await res.json();

        console.log(
          "Single Property Data:",
          data
        );

        if (!res.ok || data?.error) {
          throw new Error(
            data?.error ||
              "Unable to load property."
          );
        }

        const records = Array.isArray(data?.properties)
          ? data.properties
          : Array.isArray(data?.Property)
            ? data.Property
            : [];
        const normalized = records.map(normalizeProperty);
        const requested = String(ref).toLowerCase();
        const matched = normalized.find((item) => {
          const reference = String(item.Reference || "").toLowerCase();
          const slug = String(item.slug || "").toLowerCase();
          return reference === requested || slug === requested || requested.endsWith(`-${reference}`);
        });
        setProperty(matched || null);
      } catch (err) {
        console.error(
          "Failed to load property:",
          err
        );

        setError(err.message);
        setProperty(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [ref, listingType]);

  return {
    property,
    loading,
    error,
  };
};